import type { IbexaBaseItemFields } from '@@/types/Ibexa'
import type IbexaContentTypeIdentifier from '@@/enums/IbexaContentTypeIdentifier'

export function removeXmlTags(xml: string): string {
  const pattern = /^<\?xml [^>]*\?>|<\?xml version="1.0"\?>/g
  const modifiedXml = xml?.replace(pattern, '')

  if (modifiedXml === '\n') {
    return ''
  }

  return modifiedXml
}

export const removeHtmlTags = (str: string): string => {
  return str.replace(/<\/?[^>]+(>|$)/g, '')
}

export const removeSoftHyphen = (string: string): string => string.replaceAll(/&shy;/g, '')

export function stripStringFromEndToSlash(word: string): string {
  const parts = word.split('/')

  // Extract the last part up to the first "/"
  return parts[parts.length - 1].split(' ')[0]
}

export const snakeToCamelCase = (str: string): string =>
  str.toLowerCase().replace(/([-_][a-z])/g, group =>
    group
      .toUpperCase()
      .replace('-', '')
      .replace('_', ''),
  )

function transform<T>(
  initialValue: T,
  ...transformers: ((value: T) => T)[]
) {
  return transformers.reduce((acc, transformer) => {
    return transformer(acc)
  }, initialValue)
}

export const createSlug = (title: string, id: number | string): string => {
  return transform(
    title,
    removeHtmlTags,
    s => s.replaceAll(' ', '-'),
    removeSoftHyphen,
    encodeURIComponent,
    s => `${s}_id:${id?.toString()}`,
  )
}

export const getSlugId = (slug: string | string[]): number => {
  slug = Array.isArray(slug) ? slug[0] : slug
  return parseInt(slug?.split('_id:')[1])
}

export const parseSlug = (slug: string): string => {
  return slug.split('_id:')[0].replaceAll('-', ' ')
}

// Used to format the program label for a subject ("SF VG1" or "YK VG2" etc.)
export const formatProgramLabel = (program: string, level: string) => {
  let shortProgramName = undefined

  if (program === 'Studieforberedende') {
    shortProgramName = 'SF'
  }

  if (program === 'Yrkesfaglig') {
    shortProgramName = 'YK'
  }

  if (!shortProgramName || !level) {
    return undefined
  }

  return `${shortProgramName} ${level}`.toUpperCase()
}

export const depthFilter = (parentItem: IbexaBaseItemFields | undefined, childItem: IbexaBaseItemFields, maxDepth: number) => {
  if (!parentItem) return false
  return childItem.depth - parentItem.depth <= maxDepth
}

export const generateRandomString = (length = 10) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  return [...Array(length)].map(() => characters[Math.floor(Math.random() * characters.length)]).join('')
}

export const isContentType = (contentObject: IbexaBaseItemFields, contentType: IbexaContentTypeIdentifier) => contentObject.contentTypeIdentifier === contentType

export const randomIntValue = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1) + min)
}

export const includesSubstring = (str: string | undefined, substr: string | undefined) => {
  if (!str || !substr) return false
  return str.toLowerCase().includes(substr.toLowerCase())
}

type Falsy = false | 0 | '' | null | undefined
type Truthy<T> = Exclude<T, Falsy>
export const truthy = <T>(value: unknown): value is Truthy<T> => Boolean(value)
